/** @jsxImportSource theme-ui */
import React from 'react';
import { Flex, ThemeUIStyleObject } from 'theme-ui';

import { RiskLikelihood, RiskMagnitude } from 'models/RiskCategories';
import theme from 'theme';

import { TypographyColors } from 'components/_common/Typography/Typography';

import { BdNoDataIcon } from '../BdNoDataIcon';

const getEllipseSize = (diseaseRiskMagnitude: RiskMagnitude): number => {
  switch (diseaseRiskMagnitude) {
    case 'Not calculated':
    case 'Negligible':
    case 'Up to 10 cases':
      return 24;
    case '11 to 100 cases':
      return 28;
    case '101 to 1,000 cases':
      return 32;
    case '>1,000 cases':
      return 36;
    default:
      return 24;
  }
};

type IconColors = {
  color: keyof typeof TypographyColors;
  backgroundColor: keyof typeof TypographyColors;
};
const getIconColours = (riskLikelihood: RiskLikelihood, dark: boolean): IconColors => {
  switch (riskLikelihood) {
    case 'Unlikely':
      return dark
        ? { color: 'stone20', backgroundColor: 'stone50' }
        : { color: 'stone70', backgroundColor: 'stone50' };
    case 'Low':
      return dark
        ? { color: 'sea30', backgroundColor: 'sea90' }
        : { color: 'sea90', backgroundColor: 'sea90' };
    case 'Moderate':
      return dark
        ? { color: 'sunflower60', backgroundColor: 'sunflower100' }
        : { color: 'sunflower70', backgroundColor: 'sunflower100' };
    case 'High':
      return dark
        ? { color: 'saffron20', backgroundColor: 'saffron70' }
        : { color: 'saffron70', backgroundColor: 'saffron70' };
    case 'Very high':
      return dark
        ? { color: 'starfish40', backgroundColor: 'starfish100' }
        : { color: 'starfish100', backgroundColor: 'starfish100' };
    case 'Not calculated':
    default:
      return dark
        ? { color: 'stone20', backgroundColor: 'stone50' }
        : { color: 'stone70', backgroundColor: 'stone50' };
  }
};

type BdImportationRiskIconProps = {
  /** The bigger the risk, the bigger the ellipsis size */
  riskMagnitude: RiskMagnitude;
  /** Controls the colour of the icon */
  riskLikelihood: RiskLikelihood;
  /** For display on dark backgrounds */
  dark?: boolean;
};

const BdImportationRiskIcon: React.FC<BdImportationRiskIconProps> = ({
  riskMagnitude,
  riskLikelihood,
  dark = false,
}) => {
  const noLikelihoodData =
    riskLikelihood === 'Not calculated' || riskLikelihood === 'Calculating, revisit later!';
  const ellipseSize = getEllipseSize(riskMagnitude);
  const { backgroundColor, color } = getIconColours(riskLikelihood, dark);
  const iconStyle: ThemeUIStyleObject = {
    overflow: 'auto',
    margin: 'auto',
    position: 'absolute',
    top: '0',
    bottom: '0',
    right: '0',
    left: '0',
  };
  const iconWrapperStyle: ThemeUIStyleObject = {
    position: 'relative',
    minHeight: '36px',
    minWidth: '36px',
    justifyContent: 'center',
    alignItems: 'center',
  };

  return (
    <Flex data-testid="importation-risk-icon" sx={iconWrapperStyle}>
      {noLikelihoodData ? (
        <BdNoDataIcon dark={dark} />
      ) : (
        <>
          <div
            data-testid="ellipse-icon"
            sx={{
              width: ellipseSize,
              height: ellipseSize,
              borderRadius: '50%',
              backgroundColor: theme.colors[backgroundColor],
              opacity: 0.15,
            }}
          />
          <svg
            sx={iconStyle}
            data-testid="importation-risk"
            width="36"
            height="36"
            viewBox="0 0 36 36"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              data-testid="path"
              d="M23.9515 18.0361C24.0716 18.2315 24.1409 18.4544 24.1531 18.6841C24.1653 18.9139 24.12 19.143 24.0213 19.3503C23.959 19.4847 23.8741 19.607 23.7703 19.7119C23.6111 19.8797 23.4116 20.0028 23.1914 20.0695C22.9712 20.1362 22.7377 20.1441 22.5135 20.0924L13.7137 18.0102C13.6234 17.9893 13.5413 17.9419 13.4776 17.8738C13.414 17.8057 13.3718 17.72 13.3563 17.6276L12.7638 14.2011C12.7565 14.1317 12.7767 14.0622 12.82 14.0079C12.8633 13.9536 12.9262 13.9189 12.9947 13.9115C13.0152 13.9086 13.0359 13.9088 13.0563 13.9122L14.6283 14.0115C14.6727 14.0164 14.7151 14.0326 14.7515 14.0587C14.7879 14.0848 14.8171 14.1198 14.8364 14.1605L15.2609 15.849L17.5146 16.2546L16.5557 12.1342C16.5491 12.0477 16.5767 11.962 16.6324 11.8959C16.6881 11.8298 16.7674 11.7888 16.853 11.7817C16.8765 11.7802 16.9002 11.7811 16.9235 11.7845L17.9944 11.9447C18.0475 11.9529 18.0978 11.9743 18.1408 12.007C18.1837 12.0397 18.218 12.0827 18.2405 12.1321L20.4174 16.9492L23.1164 17.464C23.287 17.496 23.4492 17.5634 23.5929 17.6618C23.7367 17.7603 23.8587 17.8877 23.9515 18.0361Z"
              stroke={theme.colors[color]}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <line
              data-testid="line"
              x1="11.6562"
              y1="22.6719"
              x2="24.3437"
              y2="22.6719"
              stroke={theme.colors[color]}
              strokeLinecap="round"
            />
          </svg>
        </>
      )}
    </Flex>
  );
};

export default BdImportationRiskIcon;
